import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  MoreVertical,
  Pencil,
  Trash2,
  KeyRound,
  Smartphone,
  Download,
  Eye,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { hasPermission } from "../../../utils/permissionUtils";

const MENU_WIDTH = 220;

/**
 * Menu thao tác trên dòng GV đối tác (portal + Framer Motion).
 */
const TeacherAccountRowActionMenu = ({
  teacher,
  isOpen,
  onOpenChange,
  onResetPassword,
  onChangeDevice,
  onGrantQuota,
  onViewDetail,
  onEdit,
  onDelete,
}) => {
  const buttonWrapRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPos, setMenuPos] = useState(null);

  const canUpdate = hasPermission("crm.teacher_accounts", "U");
  const canDelete = hasPermission("crm.teacher_accounts", "D");
  const canRead = hasPermission("crm.teacher_accounts", "R");
  const hasAnyAction = canRead || canUpdate || canDelete;

  const updateMenuPosition = () => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const left = Math.min(
      Math.max(8, rect.right - MENU_WIDTH),
      window.innerWidth - MENU_WIDTH - 8
    );
    // Ưu tiên mở xuống; nếu sát đáy thì mở lên
    const spaceBelow = window.innerHeight - rect.bottom;
    const estimatedHeight = 260;
    const openUp = spaceBelow < estimatedHeight && rect.top > estimatedHeight;
    setMenuPos({
      top: openUp ? undefined : rect.bottom + 4,
      bottom: openUp ? window.innerHeight - rect.top + 4 : undefined,
      left,
    });
  };

  useLayoutEffect(() => {
    if (!isOpen) {
      setMenuPos(null);
      return;
    }
    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!hasAnyAction || !isOpen) return undefined;
    const handleOutside = (e) => {
      const inButton = buttonWrapRef.current?.contains(e.target);
      const inMenu = menuRef.current?.contains(e.target);
      if (!inButton && !inMenu) onOpenChange(false);
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [hasAnyAction, isOpen, onOpenChange]);

  if (!hasAnyAction) return null;

  const itemClass =
    "flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-xs font-bold text-gray-600 hover:bg-gray-50 hover:text-gray-900";

  const menuPanel = (
    <AnimatePresence>
      {isOpen && menuPos ? (
        <motion.div
          ref={menuRef}
          initial={{ opacity: 0, scale: 0.95, y: 4 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 4 }}
          style={{
            position: "fixed",
            top: menuPos.top,
            bottom: menuPos.bottom,
            left: menuPos.left,
            width: MENU_WIDTH,
            zIndex: 9999,
          }}
          className="rounded-2xl border border-gray-100 bg-white p-1.5 shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          {canRead ? (
            <button
              type="button"
              onClick={() => {
                onViewDetail?.(teacher);
                onOpenChange(false);
              }}
              className={itemClass}
            >
              <Eye size={15} className="text-red-600" />
              Xem chi tiết
            </button>
          ) : null}

          {canUpdate ? (
            <>
              <button
                type="button"
                onClick={() => {
                  onResetPassword?.(teacher);
                  onOpenChange(false);
                }}
                className={itemClass}
              >
                <KeyRound size={15} className="text-violet-600" />
                Reset mật khẩu
              </button>
              <button
                type="button"
                onClick={() => {
                  onChangeDevice?.(teacher);
                  onOpenChange(false);
                }}
                className={itemClass}
              >
                <Smartphone size={15} className="text-sky-600" />
                Đổi thiết bị
              </button>
              <button
                type="button"
                onClick={() => {
                  onGrantQuota?.(teacher);
                  onOpenChange(false);
                }}
                className={itemClass}
              >
                <Download size={15} className="text-emerald-600" />
                Thêm lượt tải
              </button>
            </>
          ) : null}

          {canDelete ? (
            <button
              type="button"
              onClick={() => {
                onDelete?.(teacher);
                onOpenChange(false);
              }}
              className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-xs font-bold text-red-600 hover:bg-red-50"
            >
              <Trash2 size={15} />
              Xóa
            </button>
          ) : null}

          {canUpdate ? (
            <button
              type="button"
              onClick={() => {
                onEdit?.(teacher);
                onOpenChange(false);
              }}
              className={itemClass}
            >
              <Pencil size={15} className="text-amber-600" />
              Sửa
            </button>
          ) : null}
        </motion.div>
      ) : null}
    </AnimatePresence>
  );

  return (
    <>
      <div
        className="relative inline-flex"
        ref={buttonWrapRef}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          ref={buttonRef}
          type="button"
          title="Tác vụ"
          aria-label="Mở menu tác vụ"
          aria-expanded={isOpen}
          onClick={() => onOpenChange(!isOpen)}
          className={`inline-flex h-9 w-9 items-center justify-center rounded-lg border transition-colors ${
            isOpen
              ? "border-gray-300 bg-gray-100 text-gray-700"
              : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700"
          }`}
        >
          <MoreVertical size={18} />
        </button>
      </div>
      {typeof document !== "undefined"
        ? createPortal(menuPanel, document.body)
        : null}
    </>
  );
};

export default TeacherAccountRowActionMenu;
